import React from 'react';
import {
  CheckCircleTwoTone,
  CloseOutlined,
  CopyOutlined,
  DislikeFilled,
  DislikeOutlined,
  LikeFilled,
  LikeOutlined,
} from '@ant-design/icons';
import clsx from 'clsx';
import { Button, Card, Input, Space, Tooltip, Typography } from 'antd';
import { createLogger } from '@owl-lib/logger';
import type { Feedback } from '../../api/feedback/interface';
import { Locale } from '../../context/AppProvider';
import useFeedbackHook from './useFeedbackHook';
import styles from './MessageFeedback.module.scss';

const logger = createLogger(__filename);

// Mirrors the citation form parsed by Markdown.tsx.
// eslint-disable-next-line redos/no-vulnerable
const CITATION_REGEX = /\[(\d+|x)]\(\s*(%7B[^)]+%7D)\s*\)/g;

const FEEDBACK_TYPE = 'bluejay_chat';

const OTHER_CATEGORY = 'other';

const FEEDBACK_CATEGORIES = [
  'incorrect_information',
  'missing_information',
  'incorrect_citations',
  'uncited_information',
  'too_lengthy',
  'poorly_formatted',
  OTHER_CATEGORY,
];

type Response = 'initial' | 'good' | 'bad';

const CopyAnswerComponent: React.FC<{ answer: string }> = ({ answer }) => {
  const { messages } = React.useContext(Locale);
  const [copied, setCopied] = React.useState(false);
  const copiedTimer = React.useRef<ReturnType<typeof setTimeout>>();

  React.useEffect(
    () => () => {
      if (copiedTimer.current) {
        clearTimeout(copiedTimer.current);
      }
    },
    []
  );

  const onCopyAnswer = React.useCallback(() => {
    // `navigator.clipboard` is undefined outside a secure context.
    if (!answer || !navigator.clipboard) {
      return;
    }

    navigator.clipboard
      .writeText(answer.replace(CITATION_REGEX, ''))
      .then(() => {
        if (copiedTimer.current) {
          clearTimeout(copiedTimer.current);
        }
        setCopied(true);
        copiedTimer.current = setTimeout(() => setCopied(false), 2_000);
        return;
      })
      .catch((err) => {
        logger.error('Failed to copy answer to clipboard: %o', err);
      });
  }, [answer]);

  return (
    <Tooltip open={copied} title="Copied!">
      <Button
        type="text"
        size="small"
        className={styles.actionBtn}
        aria-label={messages['claim_overview.ask_owl.feedback.copy_aria']}
        icon={
          copied ? (
            <CheckCircleTwoTone twoToneColor={['green', 'transparent']} />
          ) : (
            <CopyOutlined />
          )
        }
        onClick={onCopyAnswer}
      />
    </Tooltip>
  );
};

interface DetailProps {
  onSubmit(feedback: string): void;
}

const FeedbackDetail: React.FC<DetailProps> = ({ onSubmit }) => {
  const { messages } = React.useContext(Locale);
  const [submitted, setSubmitted] = React.useState(false);
  const [visible, setVisible] = React.useState(true);
  const [selectedKey, setSelectedKey] = React.useState('');
  const [inputValue, setInputValue] = React.useState('');

  const submit = (details: string, categoryKey?: string) => {
    const key = categoryKey ?? selectedKey;
    onSubmit(
      `There was negative feedback related to ${key}${
        details ? `: ${details}` : ''
      }`
    );
    setSelectedKey('');
    setInputValue('');
    setSubmitted(true);
  };

  const onCategoryClick = (key: string) => {
    setSelectedKey(key);
    if (key !== OTHER_CATEGORY) {
      submit('', key);
    }
  };

  if (!visible) {
    return null;
  }

  return (
    <div className={styles.detail}>
      {submitted ? (
        <Card className={styles.thanksCard}>
          {messages['claim_overview.ask_owl.feedback.thanks']}
        </Card>
      ) : (
        <Card
          title={messages['claim_overview.ask_owl.feedback.title']}
          extra={<CloseOutlined onClick={() => setVisible(false)} />}
          className={styles.card}
        >
          <Space orientation="vertical">
            <Space orientation="horizontal" className={styles.chips}>
              {FEEDBACK_CATEGORIES.map((key) => (
                <Button
                  key={key}
                  className={clsx(styles.chip, {
                    [styles.chipSelected]: key === selectedKey,
                  })}
                  shape="round"
                  onClick={() => onCategoryClick(key)}
                >
                  {messages[`claim_overview.ask_owl.feedback.${key}`]}
                </Button>
              ))}
            </Space>
            {selectedKey === OTHER_CATEGORY && (
              <div>
                <Input
                  placeholder={
                    messages['claim_overview.ask_owl.feedback.details']
                  }
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  className={styles.input}
                />
                <div className={styles.submitRow}>
                  <Button
                    type="primary"
                    className={styles.submitBtn}
                    onClick={() => submit(inputValue)}
                  >
                    {messages['claim_overview.ask_owl.feedback.submit']}
                  </Button>
                </div>
              </div>
            )}
          </Space>
        </Card>
      )}
    </div>
  );
};

interface Props {
  answer: string;
  query?: string;
  dossierId?: string;
  threadId?: string;
  messageId?: string;
}

const MessageFeedback: React.FC<Props> = ({
  answer,
  query,
  dossierId,
  threadId,
  messageId,
}) => {
  const { messages } = React.useContext(Locale);
  const [response, setResponse] = React.useState<Response>('initial');
  const [, handler] = useFeedbackHook();

  const send = React.useCallback(
    (feedback: string, isPositive: boolean) => {
      const data: Record<string, string> = {};
      if (threadId) {
        data.thread_id = threadId;
      }
      if (messageId) {
        data.message_id = messageId;
      }
      const payload: Feedback = {
        feedback,
        type: FEEDBACK_TYPE,
        is_positive_feedback: isPositive,
        dossier_id: dossierId,
        query,
        answer,
        data,
      };
      handler.addFeedback(payload);
    },
    [answer, query, dossierId, threadId, messageId, handler]
  );

  return (
    <div className={styles.root}>
      <div className={styles.actionsRow}>
        <CopyAnswerComponent answer={answer} />
        {response === 'initial' && (
          <>
            <Button
              type="text"
              size="small"
              className={styles.actionBtn}
              aria-label={messages['claim_overview.ask_owl.feedback.like_aria']}
              icon={<LikeOutlined />}
              onClick={() => {
                setResponse('good');
                send('The response was good.', true);
              }}
            />
            <Button
              type="text"
              size="small"
              className={styles.actionBtn}
              aria-label={
                messages['claim_overview.ask_owl.feedback.dislike_aria']
              }
              icon={<DislikeOutlined />}
              onClick={() => {
                setResponse('bad');
                send('The response was bad.', false);
              }}
            />
          </>
        )}
        {response === 'good' && (
          <>
            <Button
              type="text"
              size="small"
              className={styles.actionBtn}
              aria-label={
                messages['claim_overview.ask_owl.feedback.like_undo_aria']
              }
              icon={<LikeFilled />}
              onClick={() => setResponse('initial')}
            />
            <Typography.Text className={styles.responseLabel}>
              {messages['claim_overview.ask_owl.feedback.good_response']}
            </Typography.Text>
          </>
        )}
        {response === 'bad' && (
          <>
            <Button
              type="text"
              size="small"
              className={styles.actionBtn}
              aria-label={
                messages['claim_overview.ask_owl.feedback.dislike_undo_aria']
              }
              icon={<DislikeFilled />}
              onClick={() => setResponse('initial')}
            />
            <Typography.Text className={styles.responseLabel}>
              {messages['claim_overview.ask_owl.feedback.bad_response']}
            </Typography.Text>
          </>
        )}
      </div>
      {response === 'bad' && (
        <FeedbackDetail onSubmit={(feedback) => send(feedback, false)} />
      )}
    </div>
  );
};

export default MessageFeedback;
